import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { DetailData, useApiClient } from '@/lib/api';
import { Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

interface DetailsViewProps {
    employmentId?: string;
    educationId?: string;
    profileSectionId?: string;
    details?: DetailData[];
    onDetailsChange?: (details: DetailData[]) => void;
}

const DetailsView: React.FC<DetailsViewProps> = ({
    employmentId,
    educationId,
    profileSectionId,
    details: initialDetails,
    onDetailsChange,
}) => {

    const { api } = useApiClient();
    const [newDetail, setNewDetail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(initialDetails === undefined);
    const [details, setDetails] = useState<DetailData[]>(initialDetails || []);
    const [activeDetailId, setActiveDetailId] = useState<string | null>(null);
    const [editingDetailId, setEditingDetailId] = useState<string | null>(null);
    const [editedDetail, setEditedDetail] = useState('');

    const updateDetails = useCallback((nextDetails: DetailData[] | ((prevDetails: DetailData[]) => DetailData[])) => {
        setDetails(prevDetails => {
            const resolvedDetails = typeof nextDetails === 'function' ? nextDetails(prevDetails) : nextDetails;
            onDetailsChange?.(resolvedDetails);
            return resolvedDetails;
        });
    }, [onDetailsChange]);

    useEffect(() => {
        if (initialDetails !== undefined) {
            setDetails(initialDetails);
            setIsInitialLoading(false);
            return;
        }

        const fetchDetails = async () => {
            setIsInitialLoading(true);
            try {
                let fetchedDetails: DetailData[] = [];
                if (employmentId) {
                    fetchedDetails = await api.getDetailsByEmploymentId(employmentId);
                } else if (educationId) {
                    fetchedDetails = await api.getDetailsByEducationId(educationId);
                } else if (profileSectionId) {
                    fetchedDetails = await api.getDetailsByProfileSectionId(profileSectionId);
                } else {
                    const allDetails = await api.getDetails();
                    fetchedDetails = allDetails.filter(
                        detail => !detail.employment_id && !detail.education_id && !detail.profile_section_id
                    );
                }
                updateDetails(fetchedDetails);
            } catch (error) {
                console.error('Failed to fetch details', error);
            } finally {
                setIsInitialLoading(false);
            }
        };

        fetchDetails();
    }, [api, employmentId, educationId, profileSectionId, initialDetails, updateDetails]);

    const handleAddDetail = async () => {
        if (!newDetail.trim()) return;

        setIsLoading(true);
        try {
            const detailToAdd = {
                content: newDetail,
                employment_id: employmentId,
                education_id: educationId,
                profile_section_id: profileSectionId,
            };
            const createdDetail = await api.createDetail(detailToAdd);

            updateDetails(prevDetails => [...prevDetails, {
                id: createdDetail.id,
                content: createdDetail.content,
                employment_id: createdDetail.employment_id,
                education_id: createdDetail.education_id,
                profile_section_id: createdDetail.profile_section_id,
            }]);
            setNewDetail('');
        } catch (error) {
            console.error('Failed to add detail', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteDetail = async (id: string) => {
        setIsLoading(true);
        try {
            await api.deleteDetail(id);
            updateDetails(prevDetails => prevDetails.filter(detail => detail.id !== id));
        } catch (error) {
            console.error('Failed to delete detail', error);
        } finally {
            setIsLoading(false);
        }
    };

    const startEditingDetail = (detail: DetailData) => {
        setActiveDetailId(detail.id || null);
        setEditingDetailId(detail.id || null);
        setEditedDetail(detail.content);
    };

    const cancelEditingDetail = () => {
        setEditingDetailId(null);
        setEditedDetail('');
        setActiveDetailId(null);
    };

    const handleSaveDetail = async (detail: DetailData) => {
        if (!detail.id || !editedDetail.trim()) return;

        setIsLoading(true);
        try {
            const updatedDetail = await api.updateDetail(detail.id, {
                content: editedDetail.trim(),
                education_id: detail.education_id ?? educationId,
                employment_id: detail.employment_id ?? employmentId,
                profile_section_id: detail.profile_section_id ?? profileSectionId,
            });
            updateDetails(prevDetails => prevDetails.map(item => item.id === detail.id ? updatedDetail : item));
            cancelEditingDetail();
        } catch (error) {
            console.error('Failed to update detail', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (isInitialLoading) {
        return (
            <div className="space-y-4 animate-pulse">
                <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                </div>
                <div className="h-20 bg-gray-100 rounded"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div>
                {details.length > 0 ? (
                    <ul className="space-y-2">
                        {details.map(detail => (
                            <li key={detail.id} className="flex items-start justify-between gap-2">
                                {editingDetailId === detail.id ? (
                                    <Textarea
                                        value={editedDetail}
                                        onChange={e => setEditedDetail(e.target.value)}
                                        className="min-h-[72px] flex-1 text-sm"
                                        disabled={isLoading}
                                    />
                                ) : (
                                    <span className="flex-1 text-sm">{detail.content}</span>
                                )}
                                <div className="flex shrink-0 items-center gap-1">
                                    {activeDetailId === detail.id ? (
                                        <>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7"
                                                onClick={() => handleSaveDetail(detail)}
                                                disabled={isLoading || !editedDetail.trim()}
                                                aria-label="Save detail"
                                            >
                                                <Save className="h-3.5 w-3.5 text-green-600" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7"
                                                onClick={cancelEditingDetail}
                                                disabled={isLoading}
                                                aria-label="Cancel editing detail"
                                            >
                                                <X className="h-3.5 w-3.5 text-gray-500" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7"
                                                onClick={() => handleDeleteDetail(detail.id!)}
                                                disabled={isLoading}
                                                aria-label="Delete detail"
                                            >
                                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                            </Button>
                                        </>
                                    ) : (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() => startEditingDetail(detail)}
                                            disabled={isLoading}
                                            aria-label="Edit detail"
                                        >
                                            <Pencil className="h-3.5 w-3.5 text-interview-blue" />
                                        </Button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-sm text-gray-500 italic">No details added yet.</p>
                )}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                <div className="flex-1">
                    <Textarea
                        value={newDetail}
                        onChange={e => setNewDetail(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey && !isLoading && newDetail.trim()) {
                                e.preventDefault();
                                handleAddDetail();
                            }
                        }}
                        placeholder="e.g., Coordinated schedules, prepared reports, or supported customer requests..."
                        className="min-h-[80px]"
                        disabled={isLoading}
                    />
                </div>
                <Button
                    variant="default"
                    size="icon"
                    onClick={handleAddDetail}
                    disabled={isLoading || !newDetail.trim()}
                    className="h-10 w-10 rounded-full bg-interview-blue text-white hover:bg-interview-blue/90"
                    aria-label={isLoading ? 'Adding detail' : 'Add detail'}
                >
                    <Plus className="h-5 w-5" />
                </Button>
            </div>
        </div>
    );
};

export default DetailsView;
